import { DatePicker, Table, TableColumnsType, Select } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getRetiredStudents } from '../../../api/statistics.api';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import config from '../../../lib/config';
import exportTable from '../../../lib/exportTable';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  IGetRetiredStudents,
  IRetiredStudent,
  IRetiredStudentParams,
} from '../../../models/statistics.model';
import { RootState } from '../../../store/rootReducer';
import Button from '../../Button';
import DownloadButton from '../../DownloadButton';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import Pagination from '../../Pagination';
import MultiCampusModal from '../regular-test/modals/MultiCampusModal';
import { openTestResults } from '../../../api/tutor.api';
import TableEmpty from '../../common/TableEmpty';
import SelectCampusMultiChoice from '../../SelectCampusMultiChoice';

const List = () => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // PR0002 = 학기유형
  const PR0002 = commonCodes.find((item) => item.commonCode === 'PR0002')?.detailCode;
  const { infoNotification } = useNotification();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetRetiredStudents>();
  // for excel download
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetRetiredStudents>();

  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);
  const [srtDate, setSrtDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [data, setData] = useState<any>([]);
  const [isFirstRun, setIsFirstRun] = useState(true);
  const [semesterType, setSemesterType] = useState("RG");

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }

    doGetRetiredStudents();
  }, [pageNo]);

  useEffect(() => {
    if (res && !isLoading) {
      setData(handleData(res.data));
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      const currentDate = moment(new Date());
      exportTable(
        columns,
        handleData(res2.data),
        `${t('statistics.studentRetire.1')}_(${currentDate.format('YYYYMMDD')})`,
        t('statistics.studentEnrolled.8'),
        [12, 15, 12, 10, 12, 15, 10, 10, 20, 15, 15, 20],
      );
    }
  }, [res2, isLoading2]);

  const doGetRetiredStudents = (_pageNo?: number) => {
    if (!srtDate || !endDate) {
      return infoNotification(t('statistics.studentRetire.message1'));
    }

    const req: IRetiredStudentParams = {
      perPage: config.pageSize,
      pageNo: _pageNo || pageNo,
      srtDate,
      endDate,
      campusCode:
        auth.hqYn === 'Y'
          ? campusCodes
          : campusCodes.length
          ? campusCodes
          : auth.campus,
      semesterType,
    };

    execute(getRetiredStudents(req));
  };

  const handleData = (data: any) => {
    const _data: any = [];
    data?.forEach((campus: any) => {
      campus.studentInfo.forEach((student: any) => {
        const item: any = {
          ...student,
          campusCode: campus.campusCode,
          campusEngName: campus.campusEngName,
          campusKorName: campus.campusKorName,
        };

        item.gradeCode = getCommonCodeLabel('SY0006', item.gradeCode, 'ko');
        item.retireRsnType = getCommonCodeLabel('CL0004', item.retireRsnType || '', 'ko')
        item.studySrtDate = moment(item.studySrtDate).format('YYYY.MM.DD');
        item.retireDate = moment(item.retireDate).format('YYYY.MM.DD');

        _data.push(item);
      });
    });

    return _data;
  };

  const onExportTable = () => {
    execute2(
      getRetiredStudents({
        perPage: 9999999,
        pageNo: 1,
        srtDate,
        endDate,
        campusCode:
          auth.hqYn === 'Y'
            ? campusCodes
            : campusCodes.length
            ? campusCodes
            : auth.campus,
        semesterType,
      }),
    );
  };

  const columns: TableColumnsType<IRetiredStudent> = [
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusKorName',
      key: 'campusKorName',
      align: 'center',
    },
    {
      title: t('common.column.semester'),
      dataIndex: 'semesterName',
      key: 'semesterName',
      align: 'center',
    },
    {
      title: t('statistics.studentRetire.column6'),
      dataIndex: 'memberCode',
      key: 'memberCode',
      align: 'center',
    },
    {
      title: t('student.student.column5'),
      dataIndex: 'memberKorName',
      key: 'memberKorName',
      align: 'center',
    },
    {
      title: t('auth.login.label1'),
      dataIndex: 'webId',
      key: 'webId',
      align: 'center',
    },
    {
      title: t('statistics.studentRetire.school'),
      dataIndex: 'schoolName',
      key: 'schoolName',
      align: 'center',
    },
    {
      title: t('entrance.statistics.column2'),
      dataIndex: 'gradeCode',
      key: 'gradeCode',
      align: 'center',
      render: (value) => getCommonCodeLabel('SY0006', value, 'ko'),
    },
    {
      title: t('statistics.dttt.column1'),
      dataIndex: 'courseName',
      key: 'courseName',
      align: 'center',
    },
    {
      title: t('common.menu.class'),
      dataIndex: 'className',
      key: 'className',
      align: 'center',
    },
    {
      title: t('statistics.studentRetire.srtDate'),
      dataIndex: 'studySrtDate',
      key: 'studySrtDate',
      align: 'center',
      render: (value) => (value ? moment(value).format('YYYY.MM.DD') : '-'),
    },
    {
      title: t('statistics.studentRetire.column7'),
      dataIndex: 'retireDate',
      key: 'retireDate',
      align: 'center',
      render: (value) => (value ? moment(value).format('YYYY.MM.DD') : '-'),
    },
    {
      title: t('statistics.studentRetire.column8'),
      dataIndex: 'retireRsnType',
      key: 'retireRsnType',
      align: 'center',
      render: (value) => getCommonCodeLabel('CL0004', value || '', 'ko'),
    },
  ];

  return (
    <>
      {(isLoading || isLoading2) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />

      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx">
            <li>
              <label>{t('common.menu.campus')}</label>
              <SelectCampusMultiChoice
                placeholder={t('statistics.eLearning.title2')}
                onChange={setCampusCodes}
                value={campusCodes}
              />
              <label>
                (
                {campusCodes.length
                  ? `${campusCodes.length}${t('statistics.attendance.text1')}`
                  : t('common.text.all')}
                )
              </label>
            </li>
            <li>
              <label>{t('sms.list.column1')}</label>{/* 기간 */}
              <div className="date-picker__area">
                <DatePicker
                  value={srtDate ? moment(srtDate) : undefined}
                  onChange={(e) => {
                    if (e === null) setSrtDate('');
                    else setSrtDate(moment(e).format('YYYYMMDD'));
                  }}
                  format={['YYYY-MM-DD', 'YYYYMMDD']}
                />
              </div>
              ~
              <div className="date-picker__area">
                <DatePicker
                  value={endDate ? moment(endDate) : undefined}
                  onChange={(e) => {
                    if (e === null) setEndDate('');
                    else setEndDate(moment(e).format('YYYYMMDD'));
                  }}
                  format={['YYYY-MM-DD', 'YYYYMMDD']}
                />
              </div>
            </li>
            <li>
              <label>
                {t('program.semester.column11')}
              </label>
              <Select
                className="w-120p"
                value={semesterType}
                onChange={value => setSemesterType(value)}
              >
                {
                  PR0002?.map(({korName: label, detailCode: value}) => {
                    return (
                      <Select.Option key={value} value={value}>
                        {label}
                      </Select.Option>
                    )
                  })
                }
              </Select>
              <div className='comm-btn__area'>
                <Button
                  className='btn-basic small black'
                  onClick={() => {
                    setPageNo(1);
                    doGetRetiredStudents(1);
                  }}
                  text={t('common.button.search')}
                />
              </div>
            </li>
          </ul>
        </div>
      </div>
      {res?.data?.length ? (
        <>
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            Total: {<b>{res?.total}</b>}
          </div>
          <div>
            🏷️ 퇴원생 산정 기준은 ‘학생 퇴원처리일자’입니다. 
          </div>
        </div>			
          <div className="comm-table__area">
            <Table
              columns={columns}
              dataSource={data}
              pagination={{
                current: pageNo,
                pageSize: config.pageSize,
              }}
            />
          </div>
          <div className="comm-bx__between">
            <DownloadButton onClick={onExportTable} />
            <div className="common-paging center">
              <Pagination
                count={res?.total || 0}
                page={pageNo}
                setPage={setPageNo}
              />
            </div>
          </div>
        </>
      ) : (
        <TableEmpty text={t('statistics.appday.text1')} />
      )}

      {multiCampusVisible && (
        <MultiCampusModal
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campusCodes}
          setCampusCodes={setCampusCodes}
        />
      )}
    </>
  );
};

export default List;
